/**
 * Custom Bulk order form development for CheapTshirtUSA.com
 * All right reserved by CheapTshirtUSA © 2025.
 * Developed by Senior Shopify Designer & Developer Arafat Jahan https://arafatjahan.com
 * Version 1.1 revised 0.4
 */

;(() => {
  // Get config from liquid
  var config = window.bulkOrderConfig || {}
  var t = config.translations || {}
  var transitDays = config.transitDays || 2 // Business days added for the delivery countdown
  
  // Inventory API endpoint for real-time location quantities.
  // Migrated from the A2/PHP endpoint (api.cheaptshirtusa.com/inventory.php) to a
  // Vercel serverless function. This removes the shared-hosting WAF (Imunify360)
  // that was grey-listing IPs and blocking cross-origin inventory calls.
  var INVENTORY_API_URL = "https://inventory.cheaptshirtusa.com/api/inventory"
  
  // Variant cache settings (1 hour cache for color/size data)
  var VARIANT_CACHE_PREFIX = "cc_bulk_variants_"
  var VARIANT_CACHE_DURATION = 60 * 60 * 1000 // 1 hour in milliseconds
  
  // Inventory cache settings (5 minutes cache - balance between freshness and performance)
  var INVENTORY_CACHE_PREFIX = "cc_inventory_"
  var INVENTORY_CACHE_DURATION = 5 * 60 * 1000 // 5 minutes in milliseconds

  // Shoppers arriving from a "back in stock" email land with ?restock=1. In that
  // case we bypass BOTH inventory cache layers — our localStorage copy AND the
  // server-side inventory.php cache (via &nocache=1) — so they see fresh stock
  // immediately instead of a stale "out of stock" that could be up to 5 minutes
  // old on each layer. The fresh result is still written back to localStorage,
  // so any later navigation is fast again.
  var FORCE_FRESH_INVENTORY = /[?&]restock=1(?:&|$)/.test(window.location.search)

  // Build the inventory API URL, adding the server-side cache bypass when the
  // shopper came from a restock email.
  function buildInventoryUrl(ids) {
    var url = INVENTORY_API_URL + "?variants=" + ids
    if (FORCE_FRESH_INVENTORY) url += "&nocache=1"
    return url
  }

  // Max inventory chunk requests to run at once. Now that the endpoint is on
  // Vercel (no shared-hosting WAF to grey-list bursts), we can fetch in parallel
  // for speed. Kept at a sane cap so we don't open dozens of sockets at once.
  var INVENTORY_MAX_CONCURRENCY = 6

  function delay(ms) {
    return new Promise(function (resolve) { setTimeout(resolve, ms) })
  }

  // Fetch inventory with automatic retry + exponential backoff. Retries
  // transient network blips / cold-start hiccups instead of instantly falling
  // back to an empty (out-of-stock) table.
  function fetchInventory(url, attempts) {
    attempts = attempts || 3
    function attempt(n) {
      return fetch(url)
        .then(function (response) {
          if (!response.ok) throw new Error("HTTP " + response.status)
          return response.json()
        })
        .catch(function (err) {
          if (n < attempts) {
            // Backoff: 400ms, 800ms, 1600ms ... with light jitter.
            var wait = 400 * Math.pow(2, n - 1) + Math.floor(Math.random() * 150)
            return delay(wait).then(function () { return attempt(n + 1) })
          }
          throw err
        })
    }
    return attempt(1)
  }

  // Non-blocking banner shown when live inventory could not be loaded (e.g. the
  // host WAF is challenging the shopper's IP). Better than silently rendering
  // everything as out of stock. Injected once, with a Retry that reloads.
  function showInventoryUnavailableNotice() {
    if (document.getElementById("cc-inventory-unavailable")) return
    var bar = document.createElement("div")
    bar.id = "cc-inventory-unavailable"
    bar.setAttribute("role", "alert")
    bar.style.cssText =
      "margin:12px 0;padding:12px 16px;border:1px solid #f5c000;background:#fff8e1;" +
      "color:#5c4400;border-radius:8px;font-size:14px;line-height:1.5;text-align:center;"
    bar.innerHTML =
      "Live warehouse stock is temporarily unavailable. Quantities may not be accurate. " +
      '<button type="button" style="margin-left:8px;padding:6px 12px;border:0;border-radius:6px;' +
      'background:#d81b8c;color:#fff;font-weight:600;cursor:pointer;">Retry</button>'
    var btn = bar.querySelector("button")
    if (btn) btn.addEventListener("click", function () { window.location.reload() })

    var container =
      document.querySelector(".bulk-order-form") ||
      document.querySelector("[data-bulk-order]") ||
      document.querySelector("#bulk-order-form")
    if (container && container.parentNode) {
      container.parentNode.insertBefore(bar, container)
    } else {
      document.body.appendChild(bar)
    }
  }
  
  // Generic cache helpers
  function getCache(prefix, key, duration) {
    try {
      var cached = localStorage.getItem(prefix + key)
      if (cached) {
        var parsed = JSON.parse(cached)
        var age = Date.now() - parsed.timestamp
        if (age < duration) {
          return parsed.data
        }
        localStorage.removeItem(prefix + key)
      }
    } catch (e) {}
    return null
  }
  
  function setCache(prefix, key, data) {
    // Clean old cache first to make space
    cleanOldCache()
    
    try {
      var cacheData = JSON.stringify({
        data: data,
        timestamp: Date.now()
      })
      localStorage.setItem(prefix + key, cacheData)
    } catch (e) {
      // Clear ALL our cache and retry once
      clearAllCCCache()
      try {
        localStorage.setItem(prefix + key, cacheData)
      } catch (e2) {}
    }
  }
  
  function clearAllCCCache() {
    try {
      var prefixes = [VARIANT_CACHE_PREFIX, INVENTORY_CACHE_PREFIX, "cc_swatch_", "cc_variant_picker_"]
      var keysToRemove = []
      for (var i = 0; i < localStorage.length; i++) {
        var key = localStorage.key(i)
        if (key) {
          for (var p = 0; p < prefixes.length; p++) {
            if (key.startsWith(prefixes[p])) {
              keysToRemove.push(key)
              break
            }
          }
        }
      }
      keysToRemove.forEach(function(k) { localStorage.removeItem(k) })
    } catch (e) {}
  }
  
  function cleanOldCache() {
    try {
      var prefixes = [VARIANT_CACHE_PREFIX, INVENTORY_CACHE_PREFIX, "cc_swatch_", "cc_variant_picker_"]
      var keysToRemove = []
      for (var i = 0; i < localStorage.length; i++) {
        var key = localStorage.key(i)
        if (key) {
          for (var p = 0; p < prefixes.length; p++) {
            if (key.startsWith(prefixes[p])) {
              try {
                var item = JSON.parse(localStorage.getItem(key))
                // Remove if older than 10 minutes (more aggressive cleanup)
                if (Date.now() - item.timestamp > 10 * 60 * 1000) {
                  keysToRemove.push(key)
                }
              } catch (e) {
                keysToRemove.push(key)
              }
              break
            }
          }
        }
      }
      keysToRemove.forEach(function(k) { localStorage.removeItem(k) })
    } catch (e) {}
  }
  
  // Variant cache helpers
  function getCachedVariants(key) {
    return getCache(VARIANT_CACHE_PREFIX, key, VARIANT_CACHE_DURATION)
  }
  
  function setCachedVariants(key, data) {
    setCache(VARIANT_CACHE_PREFIX, key, data)
  }
  
  // Inventory cache helpers
  function getCachedInventory(key) {
    // Skip the local cache entirely on a restock-email landing so we always
    // fetch fresh stock (the fresh result is re-cached by setCachedInventory).
    if (FORCE_FRESH_INVENTORY) return null
    return getCache(INVENTORY_CACHE_PREFIX, key, INVENTORY_CACHE_DURATION)
  }
  
  function setCachedInventory(key, data) {
    setCache(INVENTORY_CACHE_PREFIX, key, data)
  }

  // Helper functions
  function imposeMinMax(el) {
    if (el.value !== "") {
      if (Number.parseInt(el.value) < Number.parseInt(el.min)) {
        el.value = el.min
      }
      if (Number.parseInt(el.value) > Number.parseInt(el.max)) {
        el.value = el.max
      }
    }
  }

  // Expose to global scope for inline onchange
  window.imposeMinMax = imposeMinMax

  function normalizeColor(color) {
    return color.replace(/\s+/g, "").replace(/'/g, "").replace(/\//g, "").replace(/\(/g, "").replace(/\)/g, "")
  }

  function normalizeDotColor(color) {
    return color.replace(/\s+/g, "").replace(/'/g, "").replace(/\//g, "").replace(/\./g, "").replace(/\(/g, "").replace(/\)/g, "")
  }

  function blinkCursor() {
    var searchInput = document.getElementById("dropdownSearch")
    if (searchInput) searchInput.focus()
  }

  function filterDropdown() {
    var input = document.getElementById("dropdownSearch")
    var filter = input.value.toUpperCase()
    var options = document.getElementsByClassName("SelectOptions")
    for (var i = 0; i < options.length; i++) {
      var colorName = options[i].textContent.toUpperCase()
      options[i].style.display = colorName.indexOf(filter) > -1 ? "" : "none"
    }
  }

  function formatMoney(amount, currency) {
    return new Intl.NumberFormat("en-US", { style: "currency", currency: currency || "USD" }).format(amount)
  }

  var selectedColor = ""

  // Progress bar update helper
  function updateLoadingProgress(percent, text) {
    var container = document.getElementById("bulk-order-api-container")
    if (!container) return
    var fill = container.querySelector(".loading-progress-fill")
    var percentText = container.querySelector(".loading-progress-percent")
    var statusText = container.querySelector(".loading-progress-text")
    if (fill) fill.style.width = percent + "%"
    if (percentText) percentText.textContent = percent + "%"
    if (statusText && text) statusText.textContent = text
  }

  // ModeSingle: Storefront API-based rendering (single products, no subProductHandles)
  function initModeSingle() {
    var apiContainer = document.getElementById("bulk-order-api-container")
    if (!apiContainer) return
    
    // Start progress
    updateLoadingProgress(5, "Initializing...")

    var token = apiContainer.getAttribute("data-token")
    var productId = apiContainer.getAttribute("data-product-id")
    var lowStockQty = Number.parseInt(apiContainer.getAttribute("data-low-stock")) || 11

    if (!token || token.trim() === "") {
      apiContainer.querySelector(".bulk-api-loading").style.display = "none"
      apiContainer.querySelector(".bulk-api-error").style.display = "block"
      apiContainer.querySelector(".bulk-api-error").textContent =
        "Storefront API token is missing. Please add your Storefront API token to the snippet."
      return
    }

    var allVariants = []
    var cursor = null
    
    // Get current language from HTML lang attribute or URL path
    var currentLang = document.documentElement.lang || 'en'
    // Convert to Shopify language code format (e.g., 'es' -> 'ES', 'en' -> 'EN')
    var shopifyLangCode = currentLang.split('-')[0].toUpperCase()
    
    // Cache key includes language to avoid returning wrong language data
    var cacheKey = productId + '_' + shopifyLangCode

    // Check cache for variant color/size data (1 hour cache)
    var cachedVariants = getCachedVariants(cacheKey)
    if (cachedVariants) {
      updateLoadingProgress(40, "Loading from cache...")
      // Cache hit - skip variant fetch, go straight to inventory
      fetchRealTimeInventory(cachedVariants, lowStockQty)
      return
    }

    var variantFetchCount = 0
    
    function fetchVariants() {
      updateLoadingProgress(10 + Math.min(variantFetchCount * 10, 30), "Loading product variants...")
      variantFetchCount++
      
      // Query only fetches color_code and colorswatch metafields
      // Warehouse inventory is fetched separately via real-time API
      var query =
        '\n        query getProductVariants($productId: ID!, $cursor: String, $language: LanguageCode) @inContext(language: $language) {\n          product(id: $productId) {\n            variants(first: 250, after: $cursor) {\n              pageInfo {\n                hasNextPage\n                endCursor\n              }\n              edges {\n                node {\n                  id\n                  title\n                  price {\n                    amount\n                    currencyCode\n                  }\n                  selectedOptions {\n                    name\n                    value\n                  }\n                  metafields(identifiers: [\n                    {namespace: "custom", key: "color_code"},\n                    {namespace: "custom", key: "colorswatch"}\n                  ]) {\n                    key\n                    value\n                  }\n                }\n              }\n            }\n          }\n        }\n      '

      fetch("/api/2024-01/graphql.json", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Shopify-Storefront-Access-Token": token,
        },
        body: JSON.stringify({
          query: query,
          variables: {
            productId: "gid://shopify/Product/" + productId,
            cursor: cursor,
            language: shopifyLangCode,
          },
        }),
      })
        .then((response) => response.json())
        .then((data) => {
          if (data.errors) {
            apiContainer.querySelector(".bulk-api-loading").style.display = "none"
            apiContainer.querySelector(".bulk-api-error").style.display = "block"
            apiContainer.querySelector(".bulk-api-error").textContent = "API Error: " + data.errors[0].message
            return
          }

          if (!data.data || !data.data.product) {
            apiContainer.querySelector(".bulk-api-loading").style.display = "none"
            apiContainer.querySelector(".bulk-api-error").style.display = "block"
            apiContainer.querySelector(".bulk-api-error").textContent =
              "Product not found. Please check if the product is published to the Storefront API sales channel."
            return
          }

          var variants = data.data.product.variants
          variants.edges.forEach((edge) => {
            var v = edge.node
            var variantData = {
              id: v.id.replace("gid://shopify/ProductVariant/", ""),
              gid: v.id,
              title: v.title,
              price: v.price.amount,
              currencyCode: v.price.currencyCode,
              option1: "",
              option2: "",
              metafields: {},
            }

            v.selectedOptions.forEach((opt, idx) => {
              if (idx === 0) variantData.option1 = opt.value
              if (idx === 1) variantData.option2 = opt.value
            })

            v.metafields.forEach((mf) => {
              if (mf && mf.key) {
                variantData.metafields[mf.key] = mf.value ? Number.parseInt(mf.value) || mf.value : 0
              }
            })

            allVariants.push(variantData)
          })

          if (variants.pageInfo.hasNextPage) {
            cursor = variants.pageInfo.endCursor
            fetchVariants()
          } else {
            // Save to cache (color/size data - 1 hour cache)
            setCachedVariants(cacheKey, allVariants)
            // Fetch real-time inventory from API
            fetchRealTimeInventory(allVariants, lowStockQty)
          }
        })
        .catch((error) => {
          var loadingEl = apiContainer.querySelector(".bulk-api-loading")
          var errorEl = apiContainer.querySelector(".bulk-api-error")
          if (loadingEl) loadingEl.style.display = "none"
          if (errorEl) {
            errorEl.style.display = "block"
            errorEl.textContent = "Network error: " + error.message
          }
        })
    }

    // Fetch real-time inventory from our API and merge with variant data
    function fetchRealTimeInventory(variants, lowStockQty) {
      updateLoadingProgress(50, "Loading warehouse inventory...")
      
      // Warehouse code to metafield key mapping
      var codeToKey = {
        'CN': 'fresno_ca',
        'NV': 'reno_nv',
        'TX': 'fort_worth_tx',
        'KS': 'olathe_ks',
        'OH': 'west_chester_township_oh',
        'GA': 'mcdonough_ga',
        'IL': 'lockport_il',
        'FO': 'orlando_fl',
        'MA': 'middleborough_ma',
        'PA': 'reading_pa'
      }
      
      // Check inventory cache first (5 minute cache)
      var inventoryCacheKey = productId
      var cachedInventory = getCachedInventory(inventoryCacheKey)
      if (cachedInventory) {
        updateLoadingProgress(90, "Loading from cache...")
        // Apply cached inventory to variants
        variants.forEach(function(v) {
          var inv = cachedInventory[v.id]
          if (inv && inv.inventory) {
            for (var code in inv.inventory) {
              var key = codeToKey[code]
              if (key) {
                v.metafields[key] = inv.inventory[code]
              }
            }
          }
        })
        renderBulkForm(variants, lowStockQty)
        return
      }
      
      var variantIds = variants.map(function(v) { return v.id }).join(",")
      
      // Split into chunks of 100 to avoid URL length limits
      var chunkSize = 100
      var chunks = []
      for (var i = 0; i < variants.length; i += chunkSize) {
        chunks.push(variants.slice(i, i + chunkSize))
      }
      
      var inventoryData = {}
      var completedChunks = 0
      var totalChunks = chunks.length
      var anyChunkFailed = false // track so we can warn the shopper if stock is unavailable

      function finishChunks() {
        updateLoadingProgress(95, "Preparing form...")

        // Save inventory to cache (5 minute cache)
        setCachedInventory(inventoryCacheKey, inventoryData)

        // All chunks fetched, merge inventory into variants
        variants.forEach(function(v) {
          var inv = inventoryData[v.id]
          if (inv && inv.inventory) {
            // Map API response codes to metafield keys
            for (var code in inv.inventory) {
              var key = codeToKey[code]
              if (key) {
                v.metafields[key] = inv.inventory[code]
              }
            }
          }
        })

        renderBulkForm(variants, lowStockQty)
        // If we couldn't load live stock at all, tell the shopper instead of
        // silently showing everything as out of stock.
        if (anyChunkFailed && Object.keys(inventoryData).length === 0) {
          showInventoryUnavailableNotice()
        }
      }

      var nextChunk = 0

      function onChunkSettled() {
        completedChunks++
        var completedProgress = 50 + Math.floor((completedChunks / totalChunks) * 40)
        updateLoadingProgress(completedProgress, "Loading warehouse inventory... (" + completedChunks + "/" + totalChunks + ")")
        if (completedChunks === chunks.length) {
          finishChunks()
        } else {
          launchNext()
        }
      }

      function processChunk(chunkIndex) {
        var chunk = chunks[chunkIndex]
        var ids = chunk.map(function(v) { return v.id }).join(",")

        fetchInventory(buildInventoryUrl(ids))
          .then(function(data) {
            // Merge inventory data - only store inventory object (not sku) to reduce size
            for (var variantId in data) {
              inventoryData[variantId] = { inventory: data[variantId].inventory || {} }
            }
            onChunkSettled()
          })
          .catch(function(error) {
            // Retries already exhausted inside fetchInventory. Proceed but flag
            // it so the shopper gets a clear "try again" notice.
            anyChunkFailed = true
            onChunkSettled()
          })
      }

      // Launch the next queued chunk (used to keep up to MAX_CONCURRENCY in flight).
      function launchNext() {
        if (nextChunk < chunks.length) {
          processChunk(nextChunk++)
        }
      }

      if (chunks.length > 0) {
        // Fire several chunks in parallel (bounded) for fast loading. No WAF now.
        var starters = Math.min(INVENTORY_MAX_CONCURRENCY, chunks.length)
        for (var s = 0; s < starters; s++) {
          launchNext()
        }
      } else {
        renderBulkForm(variants, lowStockQty)
      }
    }

    function renderBulkForm(variants, lowStockQty) {
      updateLoadingProgress(100, "Complete!")
      
      var colorList = []
      var colorMap = {}

      variants.forEach((v) => {
        if (!colorMap[v.option1]) {
          colorMap[v.option1] = []
          colorList.push(v.option1)
        }
        colorMap[v.option1].push(v)
      })

      var whiteColors = []
      var blackColors = []
      var otherColors = []

      colorList.forEach((c) => {
        if (c === "White" || c === "Blanc") whiteColors.push(c)
        else if (c === "Black" || c === "Noir") blackColors.push(c)
        else otherColors.push(c)
      })

      var sortedColors = whiteColors.concat(blackColors).concat(otherColors)

      var warehouses = [
        { key: "fresno_ca", name: "Fresno, CA", code: "CN" },
        { key: "reno_nv", name: "Reno, NV", code: "NV" },
        { key: "fort_worth_tx", name: "Fort Worth, TX", code: "TX" },
        { key: "olathe_ks", name: "Olathe, KS", code: "KS" },
        { key: "west_chester_township_oh", name: "West Chester, OH", code: "OH" },
        { key: "mcdonough_ga", name: "McDonough, GA", code: "GA" },
        { key: "lockport_il", name: "Lockport, IL", code: "IL" },
        { key: "orlando_fl", name: "Orlando, FL", code: "FO" },
        { key: "middleborough_ma", name: "Middleboro, MA", code: "MA" },
        { key: "reading_pa", name: "Reading, PA", code: "PA" },
      ]

      var html = ""
      html += '<div class="bulk_form_notice">' + t.bulkFormNotice + "</div>"
      html += '<div class="form-header-actions">'
      html += '<div id="colorDropdown" class="dropdown-container">'
      html += '<div class="dropdown-header FirstSelectColorOption" value="">' + t.selectColor + "</div>"
      html += '<div class="dropdownContentContainer">'
      html += '<div class="dropdownSearchContainer"><input type="text" id="dropdownSearch" placeholder="Search..." autofocus></div>'
      html += '<div class="ColorOptionsContent">'

      sortedColors.forEach((color) => {
        var firstVariant = colorMap[color][0]
        var colorCode = firstVariant.metafields.color_code || ""
        var swatchImg = firstVariant.metafields.colorswatch || ""
        var normalizedColor = color.replace(/\s+/g, "").replace(/'/g, "").replace(/\//g, "").replace(/\(/g, "").replace(/\)/g, "")
        html += '<div class="SelectOptions dropdown-hide" value="' + normalizedColor + '" data-original-color="' + color + '">'
        html +=
          '<span class="color-circle" style="background-color: ' +
          colorCode +
          "; background-image: url('" +
          swatchImg +
          "')\"></span>" +
          color
        html += "</div>"
      })

      html += "</div></div></div>"
      html += '<div class="right_side_button">'
      html += '<div class="button--secondary" id="toggleView">' + t.viewAllColors + "</div>"
      html +=
        '<div type="button" class="add-to-cart-bulk button--primary" id="add-to-cart-bulk-secondary" onclick="document.getElementById(\'add-to-cart-bulk\').click()">' +
        t.addToCart +
        "</div>"
      html += "</div></div>"

      html += '<form action="/cart/add" method="post" id="bulk-order-form">'
      html += '<div class="start-main-color-list">'

      sortedColors.forEach((color, colorIndex) => {
        var colorVariants = colorMap[color]
        var firstVariant = colorVariants[0]
        var colorCode = firstVariant.metafields.color_code || ""
        var swatchImg = firstVariant.metafields.colorswatch || ""
        var normalizedColor = color.replace(/\s+/g, "").replace(/'/g, "").replace(/\//g, "").replace(/\./g, "").replace(/\(/g, "").replace(/\)/g, "")
        var downcasedColor = color.toLowerCase()
        var orderValue =
          downcasedColor === "white" || downcasedColor === "blanc"
            ? "001"
            : downcasedColor === "black" || downcasedColor === "noir"
              ? "002"
              : String(Math.round(firstVariant.price * 100))

        html += '<div class="Color_warehouse_master_block ' + normalizedColor + '" style="order: ' + orderValue + '">'
        html += '<div class="color_name">'
        html +=
          '<span class="color_round' +
          (downcasedColor === "white" || downcasedColor === "blanc" ? " color_round_white" : "") +
          '" style="background-color: ' +
          colorCode +
          "; background-image: url('" +
          swatchImg +
          "')\"></span>"
        html += "<span>" + color + '</span><span id="swipeRightButton" class="SwipeRight">Right →</span>'
        html += "</div>"

        var hasStock = colorVariants.some((v) => warehouses.some((w) => (v.metafields[w.key] || 0) > 0))

        if (hasStock) {
          // Size/Price header row
          html += '<div class="each_warehouse_block">'
          html +=
            '<span class="warehouse_name">' + t.size + ' <br> <span class="warehouse_cat">' + t.price + "</span></span>"
          html += '<ul class="warehouse_ul">'
          colorVariants.forEach((v) => {
            html += '<li class="ai_store">'
            html += '<span class="size_block">' + v.option2 + "</span>"
            html +=
              '<div class="desc-wrapper"><span class="price_small">' +
              formatMoney(v.price, v.currencyCode) +
              "</span></div>"
            html += "</li>"
          })
          html += "</ul></div>"

          // Warehouse rows
          warehouses.forEach((w) => {
            var warehouseHasStock = colorVariants.some((v) => (v.metafields[w.key] || 0) > 0)

            if (warehouseHasStock) {
              html += '<div class="each_warehouse_block">'
              html +=
                '<span class="warehouse_name">' +
                w.name +
                '<br> <span class="warehouse_cat">' +
                t.warehouse +
                "</span></span>"
              html += '<ul class="warehouse_ul">'
              colorVariants.forEach((v) => {
                var stock = v.metafields[w.key] || 0
                if (stock > 0) {
                  html += '<li class="ai_store"><div class="desc-wrapper"><div class="input-box-price finv-btn">'
                  html +=
                    '<input size="3" type="number" class="quantity-input" name="quantity[' +
                    v.id +
                    ']" data-warehouse="' +
                    w.code +
                    '" value="" min="0" max="' +
                    stock +
                    '" onchange="imposeMinMax(this)">'
                  html +=
                    '<span class="normal_stock' + (stock < lowStockQty ? " less_stock" : "") + '">' + stock + "</span>"
                  html += "</div></div></li>"
                } else {
                  html += '<li class="ai_store"><div class="desc-wrapper"><div class="input-box-price finv-btn">'
                  // ===== BIS (Back-In-Stock notify) START — safe to remove =====
                  // Print name="quantity[ID]" + data-warehouse + data-color +
                  // data-size on the disabled out-of-stock input so the
                  // standalone back-in-stock-notify snippet can identify the
                  // variant (color + size) and warehouse and inject a "Notify
                  // me" button. Disabled inputs are never submitted to cart, so
                  // this is harmless. To remove this feature, restore the
                  // original input line (no name / data-* attributes).
                  html +=
                    '<input size="3" type="number" class="quantity-input" name="quantity[' +
                    v.id +
                    ']" data-warehouse="' +
                    w.code +
                    '" data-color="' +
                    String(color).replace(/"/g, "&quot;") +
                    '" data-size="' +
                    String(v.option2).replace(/"/g, "&quot;") +
                    '" value="0" min="0" max="0" disabled style="color:#b7b7b7;background-color:#fafafa;border-color:#e2e2e2;">'
                  // ===== BIS END =====
                  html += '<span class="stock_out_text">Out of Stock</span>'
                  html += "</div></div></li>"
                }
              })
              html += "</ul></div>"
            }
          })
        } else {
          html += '<div class="each_warehouse_block all_out_warehouse">' + t.allOutOfStock + "</div>"
        }

        html += "</div>"
      })

      html += "</div>"

      // Delivery countdown (mirrors countdown-timer.liquid; populated by runDeliveryCountdown)
      html +=
        '<div id="delivery-countdown" class="delivery-countdown"><p>' +
        '<span class="delivery-header"><span class="delivery-icon">' +
        '<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M8 2v4"></path><path d="M16 2v4"></path><rect width="18" height="18" x="3" y="4" rx="2"></rect><path d="M3 10h18"></path></svg>' +
        '</span><strong id="estimated-delivery"></strong></span>' +
        '<span id="time-left"></span></p></div>'

      html +=
        '<button type="button" class="add-to-cart-bulk add_cart_bulk button--primary no_class" id="add-to-cart-bulk">' +
        '<span class="add_to_cart_icon"><svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="8" cy="21" r="1"></circle><circle cx="19" cy="21" r="1"></circle><path d="M2.05 2.05h2l2.66 12.42a2 2 0 0 0 2 1.58h9.78a2 2 0 0 0 1.95-1.57l1.65-7.43H5.12"></path></svg></span>' +
        t.addToCart +
        "</button>"
      html += "</form>"

      // CheapTshirtUSA footer notices (matches the ModeSplit Liquid path)
      html +=
        '<p class="warehouse_notice_cartbutton order_notice_cartbutton">' +
        '<img src="https://cdn.shopify.com/s/files/1/0673/9483/0522/files/order_icon_24895ed8-a191-490a-9b94-fe4ef10c3e06.png?v=1727129184" title="How to Place a Bulk Order at CheapTshirtUSA.com" alt="How to Place a Bulk Order at CheapTshirtUSA.com" class="icon_before_status">Facing difficulties? Check our guide on ' +
        '<a href="/pages/how-to-place-a-bulk-order-at-cheaptshirtusa-com" target="_blank" title="CheapTshirtUSA Shipping Policy"> How to Place a Bulk Order ' +
        '<span class="icon_before_status"><svg width="16" height="16" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path d="M17 13.5v6H5v-12h6m3-3h6v6m0-6-9 9" class="icon_svg-stroke" stroke="#666" stroke-width="1.5" fill="none" fill-rule="evenodd" stroke-linecap="round" stroke-linejoin="round"></path></svg></span>' +
        "</a> for step-by-step instructions.</p>"

      html +=
        '<p class="warehouse_notice_cartbutton">' +
        '<img src="https://cdn.shopify.com/s/files/1/0061/1618/0086/files/additional_info_icon_9d1205d7-93df-48b1-bbcd-05950a2571eb.png?v=1711327310" title="CheapTshirtUSA Products Additional information" alt="CheapTshirtUSA Products Additional information" class="icon_before_status">Save on shipping: select items from one warehouse when possible. Shipping is charged per warehouse flat rate $12.99. Orders over $299 ship free. Choose the closest warehouse for faster delivery. ' +
        '<a href="/policies/shipping-policy" target="_blank" title="CheapTshirtUSA Shipping Policy"> Shipping policy ' +
        '<span class="icon_before_status"><svg width="16" height="16" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path d="M17 13.5v6H5v-12h6m3-3h6v6m0-6-9 9" class="icon_svg-stroke" stroke="#666" stroke-width="1.5" fill="none" fill-rule="evenodd" stroke-linecap="round" stroke-linejoin="round"></path></svg></span>' +
        "</a></p>"

      apiContainer.innerHTML = html
      initBulkFormEvents()
      runDeliveryCountdown()
    }

    fetchVariants()
  }

  // Delivery countdown - replicates snippets/countdown-timer.liquid, with configurable transit days
  function runDeliveryCountdown() {
    var estimatedEl = document.getElementById("estimated-delivery")
    var timeLeftEl = document.getElementById("time-left")
    if (!estimatedEl || !timeLeftEl) return

    var cutoffHour = 15 // 4 PM cutoff
    var weekends = [0, 6] // Saturday & Sunday

    var warehouseTimezones = {
      "America/Chicago": ["Bolingbrook, IL", "Lockport, IL", "Olathe, KS", "Fort Worth, TX", "Irving, TX"],
      "America/New_York": ["Duluth, GA", "McDonough, GA", "Middleborough, MA", "Lewisberry, PA", "Reading, PA", "Orlando, FL", "Pompano Beach, FL"],
      "America/Los_Angeles": ["Fresno, CA", "Reno, NV"],
      "America/Indiana/Indianapolis": ["West Chester Township, OH"],
    }

    function getUserTimezone() {
      return Intl.DateTimeFormat().resolvedOptions().timeZone
    }

    function getNearestWarehouseTimezone(userTimezone) {
      if (warehouseTimezones[userTimezone]) return userTimezone
      return "America/Chicago"
    }

    function getWarehouseTime() {
      var warehouseTimezone = getNearestWarehouseTimezone(getUserTimezone())
      return new Date(new Date().toLocaleString("en-US", { timeZone: warehouseTimezone }))
    }

    function getNextBusinessDay(date) {
      var day = date.getDay()
      while (weekends.indexOf(day) !== -1) {
        date.setDate(date.getDate() + 2)
        day = date.getDay()
      }
      return date
    }

    function updateCountdown() {
      var warehouseTime = getWarehouseTime()
      var cutoff = new Date(warehouseTime)
      cutoff.setHours(cutoffHour, 0, 0, 0)

      var shipDate
      if (warehouseTime < cutoff) {
        shipDate = new Date(warehouseTime)
      } else {
        shipDate = getNextBusinessDay(new Date(warehouseTime.setDate(warehouseTime.getDate() + 2)))
      }

      var deliveryDate = new Date(shipDate)
      deliveryDate = getNextBusinessDay(new Date(deliveryDate.setDate(deliveryDate.getDate() + transitDays)))

      estimatedEl.textContent =
        "Get It " + deliveryDate.toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" })

      var timeLeft = cutoff - warehouseTime
      if (timeLeft < 0) timeLeft = 0

      var hours = Math.floor(timeLeft / (1000 * 60 * 60))
      var minutes = Math.floor((timeLeft % (1000 * 60 * 60)) / (1000 * 60))

      if (hours === 0 && minutes === 0) {
        timeLeftEl.textContent = "if ordered in the next 24 hours 0 minutes"
      } else {
        timeLeftEl.textContent = "if ordered in the next " + hours + " hours " + minutes + " minutes"
      }
    }

    updateCountdown()
    setInterval(updateCountdown, 60000)
  }

  // Initialize form events (shared between ModeSingle and ModeSplit)
  function initBulkFormEvents() {
    var $ = window.jQuery
    if (!$) return

    $(".Color_warehouse_master_block").hide()
    var hasWhite = $(".Color_warehouse_master_block.White, .Color_warehouse_master_block.Blanc").length > 0
    var hasBlack = $(".Color_warehouse_master_block.Black, .Color_warehouse_master_block.Noir").length > 0
    var $preSelectedColorInput = $(".color-swatch input:checked")

    if ($preSelectedColorInput.length > 0) {
      var preSelectedColor = normalizeColor($preSelectedColorInput.closest(".color-swatch").data("color"))
      $(".Color_warehouse_master_block." + normalizeDotColor(preSelectedColor)).show()
    } else if (hasWhite) {
      $(".Color_warehouse_master_block.White, .Color_warehouse_master_block.Blanc").show()
    } else if (hasBlack) {
      $(".Color_warehouse_master_block.Black, .Color_warehouse_master_block.Noir").show()
    } else {
      $(".Color_warehouse_master_block").first().show()
    }

    var dropdownHeader = $(".dropdown-header")
    var dropdownOptions = $(".dropdownContentContainer")
    var arrow = $('<span class="arrow">&#9660;</span>')
    dropdownHeader.append(arrow)

    dropdownHeader.on("click", (event) => {
      dropdownOptions.toggle()
      arrow.html(dropdownOptions.is(":visible") ? "&#9650;" : "&#9660;")
      blinkCursor()
      event.stopPropagation()
    })

    $(".SelectOptions").on("click", function () {
      var selectedColorCircle = $(this).find(".color-circle").clone()
      var selectedColorText = $(this).text()
      arrow.html("&#9660;")
      dropdownHeader.empty().append(arrow, selectedColorCircle, selectedColorText)
      dropdownOptions.hide()
      $("#dropdownSearch").val("")
      $(".SelectOptions").show()

      var newSelectedColor = $(this).attr("value")
      $(".Color_warehouse_master_block").hide()
      $(".Color_warehouse_master_block." + normalizeDotColor(newSelectedColor)).show()
      
      // Try to find swatch by multiple methods
      var colorSwatchRadio = $(".color-swatch[data-color='" + newSelectedColor + "'] .color-swatch__radio")
      
      // Method 2: Match by background color/image (read from style attribute)
      if (!colorSwatchRadio.length) {
        var dropdownCircle = $(this).find(".color-circle")
        var dropdownStyleAttr = dropdownCircle.attr("style") || ""
        
        // Extract from dropdown
        var dropdownBgImageMatch = dropdownStyleAttr.match(/background-image:\s*url\(['"]?([^'")\s]+)['"]?\)/i)
        var dropdownBgImageUrl = dropdownBgImageMatch ? dropdownBgImageMatch[1] : ""
        var dropdownBgColorMatch = dropdownStyleAttr.match(/background-color:\s*([^;]+)/i)
        var dropdownBgColor = dropdownBgColorMatch ? dropdownBgColorMatch[1].trim() : ""
        
        $(".color-swatch").each(function() {
          var swatchLabel = $(this).find("label")
          var swatchStyleAttr = swatchLabel.attr("style") || ""
          
          // Extract from swatch
          var swatchBgImageMatch = swatchStyleAttr.match(/background-image:\s*url\(['"]?([^'")\s]+)['"]?\)/i)
          var swatchBgImageUrl = swatchBgImageMatch ? swatchBgImageMatch[1] : ""
          var swatchBgColorMatch = swatchStyleAttr.match(/background-color:\s*([^;]+)/i)
          var swatchBgColor = swatchBgColorMatch ? swatchBgColorMatch[1].trim() : ""
          
          var matchByImage = dropdownBgImageUrl && swatchBgImageUrl && dropdownBgImageUrl === swatchBgImageUrl
          var matchByColor = dropdownBgColor && swatchBgColor && dropdownBgColor === swatchBgColor
          
          if (matchByImage || matchByColor) {
            colorSwatchRadio = $(this).find(".color-swatch__radio")
            return false // break loop
          }
        })
      }
      
      if (colorSwatchRadio.length) colorSwatchRadio.click()
      $("#toggleView").text(t.viewAllColors)
      $("#add-to-cart-bulk-secondary").hide()
      selectedColor = newSelectedColor
    })

    $(document).on("click", (event) => {
      if (!$(event.target).closest(".dropdown-container").length) {
        dropdownOptions.hide()
        arrow.html("&#9660;")
      }
    })

    var searchInput = document.getElementById("dropdownSearch")
    if (searchInput) {
      searchInput.addEventListener("keyup", filterDropdown)
    }

    $("#toggleView").on("click", function () {
      var isViewAll = $(this).text() === t.viewAllColors
      if (isViewAll) {
        $(".Color_warehouse_master_block").show()
        $("#add-to-cart-bulk-secondary").show()
      } else {
        $(".Color_warehouse_master_block").hide()
        if (!selectedColor) {
          if (hasWhite) {
            $(".Color_warehouse_master_block.White, .Color_warehouse_master_block.Blanc").show()
          } else if (hasBlack) {
            $(".Color_warehouse_master_block.Black, .Color_warehouse_master_block.Noir").show()
          } else {
            $(".Color_warehouse_master_block").first().show()
          }
        } else {
          $(".Color_warehouse_master_block." + normalizeDotColor(selectedColor)).show()
        }
        $("#add-to-cart-bulk-secondary").hide()
      }
      $(this).text(isViewAll ? t.viewOneColor : t.viewAllColors)
    })

    $(".color-swatch").on("click", function () {
      var clickedColor = $(this).data("color") // This is normalized (no spaces)
      var clickedColorText = $(this).find('label').attr('title') || '' // Full color name with spaces
      var clickedColorDataValue = $(this).find('label').data('color-value') || clickedColorText // data-color-value attribute
      var selectedColorVal = normalizeColor(clickedColor)
      
      // Get swatch background style for matching - read from style attribute directly
      var swatchLabel = $(this).find('label')
      var swatchStyleAttr = swatchLabel.attr('style') || ''
      
      // Extract background-color from inline style
      var bgColorMatch = swatchStyleAttr.match(/background-color:\s*([^;]+)/i)
      var swatchBgColor = bgColorMatch ? bgColorMatch[1].trim() : ''
      
      // Extract background-image URL from inline style
      var bgImageMatch = swatchStyleAttr.match(/background-image:\s*url\(['"]?([^'")\s]+)['"]?\)/i)
      var swatchBgImageUrl = bgImageMatch ? bgImageMatch[1] : ''
      
      // Try multiple methods to find the matching dropdown option
      var dropdownOption = null
      
      // Method 1: Direct value match (works when both are same language)
      dropdownOption = $('.SelectOptions[value="' + selectedColorVal + '"]')
      
      // Method 2: Match by data-translated-color attribute (normalized translated name)
      if (!dropdownOption.length) {
        dropdownOption = $('.SelectOptions[data-translated-color="' + selectedColorVal + '"]')
      }
      
      // Method 3: Match by data-original-color attribute (API stores English name here)
      if (!dropdownOption.length) {
        dropdownOption = $('.SelectOptions').filter(function() {
          var origColor = $(this).data('original-color') || ''
          return normalizeColor(origColor) === selectedColorVal
        })
      }
      
      // Method 4: Match by displayed text (dropdown shows translated name)
      if (!dropdownOption.length && clickedColorDataValue) {
        dropdownOption = $('.SelectOptions').filter(function() {
          var optionText = $(this).text().trim()
          return optionText === clickedColorDataValue
        })
      }
      
      // Method 5: Match by title attribute
      if (!dropdownOption.length && clickedColorText) {
        dropdownOption = $('.SelectOptions').filter(function() {
          var optionText = $(this).text().trim()
          return optionText === clickedColorText
        })
      }
      
      // Method 6: Match by background color or image (same in both languages)
      if (!dropdownOption.length && (swatchBgColor || swatchBgImageUrl)) {
        dropdownOption = $('.SelectOptions').filter(function() {
          var optionCircle = $(this).find('.color-circle')
          var optionStyleAttr = optionCircle.attr('style') || ''
          
          // Extract background-image URL from dropdown option
          var optionBgImageMatch = optionStyleAttr.match(/background-image:\s*url\(['"]?([^'")\s]+)['"]?\)/i)
          var optionBgImageUrl = optionBgImageMatch ? optionBgImageMatch[1] : ''
          
          // Extract background-color from dropdown option
          var optionBgColorMatch = optionStyleAttr.match(/background-color:\s*([^;]+)/i)
          var optionBgColor = optionBgColorMatch ? optionBgColorMatch[1].trim() : ''
          
          // Match by background image URL if both have one
          if (swatchBgImageUrl && optionBgImageUrl && swatchBgImageUrl === optionBgImageUrl) {
            return true
          }
          // Match by background color if no image
          if (swatchBgColor && optionBgColor && swatchBgColor === optionBgColor) {
            return true
          }
          return false
        })
      }
      
      if (dropdownOption && dropdownOption.length) {
        var selectedColorCircle = dropdownOption.find(".color-circle").clone()
        var selectedColorText = dropdownOption.text()
        var actualColorVal = dropdownOption.attr("value")
        dropdownHeader.empty().append(arrow, selectedColorCircle, selectedColorText)
        $(".Color_warehouse_master_block").hide()
        $(".Color_warehouse_master_block." + normalizeDotColor(actualColorVal)).show()
        selectedColor = actualColorVal
      }
    })

    var addToCartBtn = document.getElementById("add-to-cart-bulk")
    if (addToCartBtn) {
      addToCartBtn.addEventListener("click", () => {
        var selectedVariants = []
        document.querySelectorAll(".quantity-input").forEach((input) => {
          // Stock-out inputs are rendered without a name attribute, so guard against null
          var inputName = input.getAttribute("name")
          var match = inputName ? inputName.match(/\[(\d+)\]/) : null
          if (match) {
            var variantId = match[1]
            var quantity = Number.parseInt(input.value)
            var warehouse = input.getAttribute("data-warehouse")
            if (quantity > 0) {
              selectedVariants.push({ id: variantId, quantity: quantity, properties: { Warehouse: warehouse } })
            }
          }
        })

        document.dispatchEvent(new CustomEvent("theme:loading:start"))

        if (selectedVariants.length > 0) {
          fetch("/cart/add.js", {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              "X-Requested-With": "xmlhttprequest",
            },
            body: JSON.stringify({ items: selectedVariants }),
          })
            .then((response) => response.json())
            .then((data) => {
              document.documentElement.dispatchEvent(
                new CustomEvent("product:added", {
                  bubbles: true,
                  detail: { selectedVariants: selectedVariants },
                }),
              )
            })
            .catch((error) => {
              console.error("Error:", error)
            })
        } else {
          alert("Please enter quantities for at least one item.")
        }
        document.querySelectorAll(".quantity-input").forEach((input) => {
          input.value = ""
        })
      })
    }
  }

  // ModeSplit: Liquid-based initialization (split/master products with subProductHandles)
  // Apply fetched inventory into the Liquid-rendered warehouse grid (split/master products)
  function applyLiquidInventory(inventoryData) {
    // Reveal the grid and remove the loading bar (covers both fetch and cache-hit paths)
    hideLiquidLoadingBar()
    // Quantity inputs live in the warehouse table, not inside #bulk-order-form, so query the document
    var inputs = document.querySelectorAll(".quantity-input")
    if (!inputs.length) return
    inputs.forEach(function (input) {
      var m = (input.getAttribute("name") || "").match(/\[(\d+)\]/)
      if (!m) return
      var variantId = m[1]
      var code = input.getAttribute("data-warehouse")
      var qty = 0
      var inv = inventoryData[variantId]
      if (inv && inv.inventory && inv.inventory[code] != null) {
        qty = Number.parseInt(inv.inventory[code]) || 0
      }
      var wrapper = input.parentNode
      var span = wrapper ? wrapper.querySelector(".normal_stock, .stock_out_text") : null
      var lowStockQty = Number.parseInt(config.lowStockQty) || 11
      if (qty > 0) {
        input.setAttribute("max", qty)
        input.setAttribute("min", 0)
        input.disabled = false
        input.style.color = "" // reset to normal color when back in stock
        input.style.backgroundColor = ""
        input.style.borderColor = ""
        if (span) {
          span.textContent = qty
          // Show low quantities in red (less_stock), normal quantities in green
          span.className = qty < lowStockQty ? "normal_stock less_stock" : "normal_stock"
        }
      } else {
        input.value = "0"
        input.setAttribute("max", 0)
        input.setAttribute("min", 0)
        input.disabled = true
        input.style.color = "#b7b7b7" // softer color for out-of-stock "0"
        input.style.backgroundColor = "#fafafa"
        input.style.borderColor = "#e2e2e2"
        if (span) {
          span.textContent = "Out of Stock"
          span.className = "stock_out_text"
        }
      }
    })

    // Hide any warehouse row where every size is out of stock (matches single-product behavior).
    // The Size/Price header block has no .quantity-input, so it is naturally left visible.
    document.querySelectorAll(".each_warehouse_block").forEach(function (block) {
      var blockInputs = block.querySelectorAll(".quantity-input")
      if (!blockInputs.length) return
      var hasStock = false
      blockInputs.forEach(function (inp) {
        if ((Number.parseInt(inp.getAttribute("max")) || 0) > 0) hasStock = true
      })
      block.style.display = hasStock ? "" : "none"
    })
  }

  // Show a loading bar over the Liquid warehouse grid (reuses the API-mode .loading-progress-* styles)
  function showLiquidLoadingBar() {
    if (document.getElementById("liquid-inventory-loading")) return
    var host = document.querySelector(".start-main-color-list") || document.body
    var overlay = document.createElement("div")
    overlay.id = "liquid-inventory-loading"
    overlay.className = "bulk-api-loading"
    overlay.innerHTML =
      '<div class="loading-progress-container">' +
      '<div class="loading-progress-text">Loading warehouse inventory...</div>' +
      '<div class="loading-progress-bar"><div class="loading-progress-fill"></div></div>' +
      '<div class="loading-progress-percent">0%</div>' +
      "</div>"
    host.parentNode.insertBefore(overlay, host)
    host.style.display = "none"
  }

  function updateLiquidLoadingProgress(percent, text) {
    var overlay = document.getElementById("liquid-inventory-loading")
    if (!overlay) return
    var fill = overlay.querySelector(".loading-progress-fill")
    var percentText = overlay.querySelector(".loading-progress-percent")
    var statusText = overlay.querySelector(".loading-progress-text")
    if (fill) fill.style.width = percent + "%"
    if (percentText) percentText.textContent = percent + "%"
    if (statusText && text) statusText.textContent = text
  }

  function hideLiquidLoadingBar() {
    var overlay = document.getElementById("liquid-inventory-loading")
    if (overlay && overlay.parentNode) overlay.parentNode.removeChild(overlay)
    var host = document.querySelector(".start-main-color-list")
    if (host) host.style.display = ""
  }

  // Fetch live inventory for the Liquid path using the variant IDs already present in the DOM
  function loadLiquidInventory() {
    // Quantity inputs are in the warehouse table (outside #bulk-order-form), so query the document
    var inputs = document.querySelectorAll(".quantity-input")
    if (!inputs.length) return

    // Collect unique variant IDs from the rendered inputs
    var seen = {}
    var ids = []
    inputs.forEach(function (input) {
      var m = (input.getAttribute("name") || "").match(/\[(\d+)\]/)
      if (m && !seen[m[1]]) {
        seen[m[1]] = true
        ids.push(m[1])
      }
    })
    if (!ids.length) return

    var productId = config.productId || "liquid"
    var inventoryCacheKey = productId + "_liquid"

    // 5-minute inventory cache (shared helpers), keeps stock fresh without re-fetching every load
    var cached = getCachedInventory(inventoryCacheKey)
    if (cached) {
      applyLiquidInventory(cached)
      return
    }

    // Show the loading bar only when we actually need to hit the network
    showLiquidLoadingBar()
    updateLiquidLoadingProgress(5, "Loading warehouse inventory...")

    // Chunk into 100s to stay under URL length limits (same as single-product path)
    var chunkSize = 100
    var chunks = []
    for (var i = 0; i < ids.length; i += chunkSize) {
      chunks.push(ids.slice(i, i + chunkSize))
    }

    var inventoryData = {}
    var completed = 0
    var totalChunks = chunks.length
    var anyChunkFailed = false

    var nextChunk = 0

    // Bounded-parallel loading. The endpoint is on Vercel now (no shared-hosting
    // WAF to grey-list bursts), so we fetch several chunks at once for speed,
    // capped at INVENTORY_MAX_CONCURRENCY sockets.
    function processLiquidChunk(index) {
      var chunk = chunks[index]
      fetchInventory(buildInventoryUrl(chunk.join(",")))
        .then(function (data) {
          for (var vId in data) {
            inventoryData[vId] = { inventory: data[vId].inventory || {} }
          }
          advance()
        })
        .catch(function () {
          anyChunkFailed = true
          advance()
        })

      function advance() {
        completed++
        updateLiquidLoadingProgress(
          Math.round((completed / totalChunks) * 100),
          "Loading warehouse inventory... (" + completed + "/" + totalChunks + ")",
        )
        if (completed === totalChunks) {
          if (Object.keys(inventoryData).length > 0) {
            setCachedInventory(inventoryCacheKey, inventoryData)
          }
          applyLiquidInventory(inventoryData)
          if (anyChunkFailed && Object.keys(inventoryData).length === 0) {
            showInventoryUnavailableNotice()
          }
        } else {
          launchNext()
        }
      }
    }

    function launchNext() {
      if (nextChunk < chunks.length) {
        processLiquidChunk(nextChunk++)
      }
    }

    if (chunks.length > 0) {
      var starters = Math.min(INVENTORY_MAX_CONCURRENCY, chunks.length)
      for (var s = 0; s < starters; s++) {
        launchNext()
      }
    } else {
      applyLiquidInventory(inventoryData)
    }
  }

  function initModeSplit() {
    // Load live warehouse inventory for split/master products (variant IDs already in the DOM).
    // Runs independently of jQuery so inventory populates even if the theme has no jQuery.
    loadLiquidInventory()

    var $ = window.jQuery
    if (!$) return

    $(".Color_warehouse_master_block").hide()
    var hasWhite = $(".Color_warehouse_master_block.White, .Color_warehouse_master_block.Blanc").length > 0
    var hasBlack = $(".Color_warehouse_master_block.Black, .Color_warehouse_master_block.Noir").length > 0
    var $preSelectedColorInput = $(".color-swatch input:checked")

    if ($preSelectedColorInput.length > 0) {
      var preSelectedColor = normalizeColor($preSelectedColorInput.closest(".color-swatch").data("color"))
      $(".Color_warehouse_master_block." + normalizeDotColor(preSelectedColor)).show()
    } else if (hasWhite) {
      $(".Color_warehouse_master_block.White, .Color_warehouse_master_block.Blanc").show()
    } else if (hasBlack) {
      $(".Color_warehouse_master_block.Black, .Color_warehouse_master_block.Noir").show()
    } else {
      $(".Color_warehouse_master_block").first().show()
    }

    $(".color-swatch").on("click", function () {
      var selectedColorVal = normalizeColor($(this).data("color"))
      var dropdownOption = $('.SelectOptions[value="' + selectedColorVal + '"]')
      var selectedColorCircle = dropdownOption.find(".color-circle").clone()
      var selectedColorText = dropdownOption.text()
      var dropdownHeader = $(".dropdown-header")
      var arrow = dropdownHeader.find(".arrow")
      if (!arrow.length) {
        arrow = $('<span class="arrow">&#9660;</span>')
      }
      dropdownHeader.empty().append(arrow, selectedColorCircle, selectedColorText)
      $(".Color_warehouse_master_block").hide()
      $(".Color_warehouse_master_block." + normalizeDotColor(selectedColorVal)).show()
      document
        .getElementById("colorDropdown")
        .dispatchEvent(new CustomEvent("colorChange", { bubbles: true, detail: { color: selectedColorVal } }))
    })

    var colorDropdown = document.getElementById("colorDropdown")
    if (colorDropdown) {
      colorDropdown.addEventListener("colorChange", (event) => {
        var newSelectedColor = event.detail.color
        $("#colorDropdown").val(newSelectedColor).trigger("change")
      })

      $(".color-swatch__radio").on("click", () => {
        $("#colorDropdown").addClass("dropdown-triggered")
      })

      colorDropdown.addEventListener("colorChange", (event) => {
        if (!event.target.classList.contains("dropdown-triggered")) {
          var newSelectedColor = event.detail.color
          if (newSelectedColor === t.viewAllColors) {
            $(".Color_warehouse_master_block").show()
            $("#toggleView").text(t.viewOneColor)
          } else {
            $(".Color_warehouse_master_block").hide()
            $(".Color_warehouse_master_block." + normalizeDotColor(newSelectedColor)).show()
            $(".color-swatch[data-color='" + newSelectedColor + "'] .color-swatch__radio").click()
            if (newSelectedColor !== selectedColor) {
              $("#toggleView").text(t.viewAllColors)
              $("#add-to-cart-bulk-secondary").hide()
            }
          }
          selectedColor = newSelectedColor
        } else {
          event.target.classList.remove("dropdown-triggered")
        }
      })

      colorDropdown.addEventListener("click", (event) => {
        if (event.target.classList.contains("SelectOptions")) {
          var newSelectedColor = event.target.getAttribute("value")
          if (newSelectedColor === t.viewAllColors) {
            document.querySelectorAll(".Color_warehouse_master_block").forEach((element) => {
              element.style.display = "block"
            })
            document.getElementById("toggleView").innerText = t.viewOneColor
          } else {
            document.querySelectorAll(".Color_warehouse_master_block").forEach((element) => {
              element.style.display = "none"
            })
            var colorDiv = document.querySelector(
              ".Color_warehouse_master_block." + normalizeDotColor(newSelectedColor),
            )
            if (colorDiv) {
              colorDiv.style.display = "block"
            }
            // Try to find swatch by multiple methods (for translation support)
            var colorSwatchRadio = document.querySelector(
              ".color-swatch[data-color='" + newSelectedColor + "'] .color-swatch__radio",
            )
            
            // Method 2: Try to find by matching background color/image (read style attribute directly)
            if (!colorSwatchRadio) {
              var dropdownOption = document.querySelector('.SelectOptions[value="' + newSelectedColor + '"]')
              if (dropdownOption) {
                var dropdownCircle = dropdownOption.querySelector(".color-circle")
                if (dropdownCircle) {
                  var dropdownStyleAttr = dropdownCircle.getAttribute("style") || ""
                  var dropdownBgImageMatch = dropdownStyleAttr.match(/background-image:\s*url\(['"]?([^'")\s]+)['"]?\)/i)
                  var dropdownBgImageUrl = dropdownBgImageMatch ? dropdownBgImageMatch[1] : ""
                  var dropdownBgColorMatch = dropdownStyleAttr.match(/background-color:\s*([^;]+)/i)
                  var dropdownBgColor = dropdownBgColorMatch ? dropdownBgColorMatch[1].trim() : ""
                  
                  document.querySelectorAll(".color-swatch").forEach(function(swatch) {
                    if (colorSwatchRadio) return
                    var swatchLabel = swatch.querySelector("label")
                    if (swatchLabel) {
                      var swatchStyleAttr = swatchLabel.getAttribute("style") || ""
                      var swatchBgImageMatch = swatchStyleAttr.match(/background-image:\s*url\(['"]?([^'")\s]+)['"]?\)/i)
                      var swatchBgImageUrl = swatchBgImageMatch ? swatchBgImageMatch[1] : ""
                      var swatchBgColorMatch = swatchStyleAttr.match(/background-color:\s*([^;]+)/i)
                      var swatchBgColor = swatchBgColorMatch ? swatchBgColorMatch[1].trim() : ""
                      
                      var matchByImage = dropdownBgImageUrl && swatchBgImageUrl && dropdownBgImageUrl === swatchBgImageUrl
                      var matchByColor = dropdownBgColor && swatchBgColor && dropdownBgColor === swatchBgColor
                      
                      if (matchByImage || matchByColor) {
                        colorSwatchRadio = swatch.querySelector(".color-swatch__radio")
                      }
                    }
                  })
                }
              }
            }
            
            if (colorSwatchRadio) {
              colorSwatchRadio.click()
            }
            if (newSelectedColor !== selectedColor) {
              document.getElementById("toggleView").innerText = t.viewAllColors
              document.getElementById("add-to-cart-bulk-secondary").style.display = "none"
            }
          }
          selectedColor = newSelectedColor
        }
      })
    }

    var dropdownHeader = $(".dropdown-header")
    var dropdownOptions = $(".dropdownContentContainer")
    var arrow = $('<span class="arrow">&#9660;</span>')
    dropdownHeader.append(arrow)

    dropdownHeader.on("click", (event) => {
      dropdownOptions.toggle()
      arrow.html(dropdownOptions.is(":visible") ? "&#9650;" : "&#9660;")
      blinkCursor()
      event.stopPropagation()
    })

    $(".SelectOptions").on("click", function () {
      var selectedColorCircle = $(this).find(".color-circle").clone()
      var selectedColorText = $(this).text()
      arrow.html("&#9660;")
      dropdownHeader.empty().append(arrow, selectedColorCircle, selectedColorText)
      dropdownOptions.hide()
      $("#dropdownSearch").val("")
      $(".SelectOptions").show()
    })

    $(document).on("click", (event) => {
      if (!$(event.target).closest(".dropdown-container").length) {
        dropdownOptions.hide()
        arrow.html("&#9660;")
      }
    })

    var searchInput = document.getElementById("dropdownSearch")
    if (searchInput) {
      searchInput.addEventListener("keyup", filterDropdown)
    }

    $("#toggleView").on("click", function () {
      var isViewAll = $(this).text() === t.viewAllColors
      if (isViewAll) {
        $(".Color_warehouse_master_block").show()
        $("#add-to-cart-bulk-secondary").show()
      } else {
        $(".Color_warehouse_master_block").hide()
        if (!selectedColor) {
          var hasWhiteToggle = $(".Color_warehouse_master_block.White, .Color_warehouse_master_block.Blanc").length > 0
          var hasBlackToggle = $(".Color_warehouse_master_block.Black, .Color_warehouse_master_block.Noir").length > 0
          if (hasWhiteToggle) {
            $(".Color_warehouse_master_block.White, .Color_warehouse_master_block.Blanc").show()
          } else if (hasBlackToggle) {
            $(".Color_warehouse_master_block.Black, .Color_warehouse_master_block.Noir").show()
          } else {
            $(".Color_warehouse_master_block").first().show()
          }
        } else {
          $(".Color_warehouse_master_block." + normalizeDotColor(selectedColor)).show()
        }
        $("#add-to-cart-bulk-secondary").hide()
      }
      $(this).text(isViewAll ? t.viewOneColor : t.viewAllColors)
    })

    // Add to cart for ModeSplit
    var addToCartBtn = document.getElementById("add-to-cart-bulk")
    if (addToCartBtn) {
      addToCartBtn.addEventListener("click", () => {
        var selectedVariants = []
        document.querySelectorAll(".quantity-input").forEach((input) => {
          // Guard against inputs without a name attribute
          var inputName = input.getAttribute("name")
          var match = inputName ? inputName.match(/\[(\d+)\]/) : null
          if (match) {
            var variantId = match[1]
            var quantity = Number.parseInt(input.value)
            var warehouse = input.getAttribute("data-warehouse")
            if (quantity > 0) {
              selectedVariants.push({ id: variantId, quantity: quantity, properties: { Warehouse: warehouse } })
            }
          }
        })

        document.dispatchEvent(new CustomEvent("theme:loading:start"))

        if (selectedVariants.length > 0) {
          fetch("/cart/add.js", {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              "X-Requested-With": "xmlhttprequest",
            },
            body: JSON.stringify({ items: selectedVariants }),
          })
            .then((response) => response.json())
            .then((data) => {
              document.documentElement.dispatchEvent(
                new CustomEvent("product:added", {
                  bubbles: true,
                  detail: { selectedVariants: selectedVariants },
                }),
              )
            })
            .catch((error) => {
              console.error("Error:", error)
            })
        } else {
          alert("Please enter quantities for at least one item.")
        }
        document.querySelectorAll(".quantity-input").forEach((input) => {
          input.value = ""
        })
      })
    }
  }

  // Main initialization
  document.addEventListener("DOMContentLoaded", () => {
    var apiContainer = document.getElementById("bulk-order-api-container")

    if (apiContainer) {
      // ModeSingle: single product, render via Storefront API
      initModeSingle()
    } else {
      // ModeSplit: split/master product, use the Liquid-rendered warehouse grid
      initModeSplit()
    }
  })
})()
